import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthState } from "../hooks/useAuth";
import { useToasts } from "../hooks/useToasts";
import { useTheme } from "../hooks/useTheme";
import Button from "./Button";
import "./Header.css";
import InboxButton from "./InboxButton";
import { User, Home, Sun, Moon, Shield, BarChart3, Users, FileText } from "lucide-react";
import { isEarlyAccess } from "../constants/contentLimits";
import { shouldShowSuperAdminStats } from "../utils/superAdminStatsAccess";

const Header = () => {
  const { user, loading } = useAuthState();
  const { addToast } = useToasts();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const showSuperAdminStats = shouldShowSuperAdminStats();

  const [currentUser, setCurrentUser] = useState(user);
  useEffect(() => {
    setCurrentUser(user);
  }, [user]);

  const [, setShowMySpaceAnimation] = useState(false);
  const [, setShowMySpaceIcon] = useState(false);
  const [, setShowMyContentAnimation] = useState(false);
  const [, setShowMyContentIcon] = useState(false);
  const [, setShowCreateAnimation] = useState(false);
  const [, setShowCreateIcon] = useState(false);

  useEffect(() => {
    if (currentUser) {
      // MySpace animation timer - only for users
      if (currentUser.role === "user") {
        const mySpaceTimer = setTimeout(() => {
          setShowMySpaceAnimation(true);
        }, 20000);

        // MyContent animation timer - same time as MySpace
        const myContentTimer = setTimeout(() => {
          setShowMyContentAnimation(true);
        }, 20000);

        // Create animation timer - same time as MySpace
        const createTimer = setTimeout(() => {
          setShowCreateAnimation(true);
        }, 20000);

        // All icons appear together after flip completes
        const iconsTimer = setTimeout(() => {
          setShowMySpaceIcon(true);
          setShowMyContentIcon(true);
          setShowCreateIcon(true);
        }, 21000);

        return () => {
          clearTimeout(mySpaceTimer);
          clearTimeout(myContentTimer);
          clearTimeout(createTimer);
          clearTimeout(iconsTimer);
        };
      }
    }
  }, [currentUser]);

  const handleLogoClick = () => {
    navigate("/");
  };

  if (loading) {
    return null;
  }

  return (
    <header className="header">
      <div className="header-title">
        <h1 className="header-logo">
          <button
            type="button"
            onClick={handleLogoClick}
            className="header-logo-button"
          >
            <span className="header-logo-text" aria-label="asasiasa">
              asa<span className="header-logo-gap" aria-hidden="true"> </span><span className="header-logo-text-accent">si</span><span className="header-logo-gap" aria-hidden="true"> </span>asa
            </span>
            {isEarlyAccess(currentUser) && <span className="header-logo-plus">+</span>}
          </button>
        </h1>
        {currentUser && (
          <div className="profile-button-wrapper">
            <button
              className="profile-button-oval"
              onClick={() => navigate("/profile")}
              title={`Profile: ${currentUser.email}`}
            >
              <span>
                <User />
              </span>
            </button>
            {currentUser.displayName &&
              currentUser.showDisplayName !== false && (
                <div className="profile-display-name">
                  {currentUser.displayName}
                </div>
              )}
          </div>
        )}
        <button
          className="header-theme-toggle-button"
          onClick={() => {
            toggleTheme();
            addToast(
              `${theme === "light" ? "Dark" : "Light"} mode ON`,
              "success-info",
            );
          }}
          title={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
        >
          {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
        </button>
      </div>
      <div className="header-right">
        {currentUser?.role === "user" && currentUser?.isActive !== false && (
          <div className="header-admin-buttons">
            <button
              className="header-myspace-mobile-button"
              onClick={() => navigate("/myspace")}
              title="My Space"
            >
              <Home size={14} />
            </button>
            <InboxButton variant="header-mobile" />
          </div>
        )}
        {currentUser?.role === "admin" && (
          <div className="header-admin-buttons">
            <div className="header-circle-buttons">
              <button
                className="header-circle-btn"
                onClick={() => navigate("/admin")}
                title="Users"
              >
                Users
              </button>
              <button
                className="header-circle-btn"
                onClick={() => navigate("/admin/content")}
                title="Content"
              >
                Content
              </button>
            </div>
            <button
              className="header-myspace-mobile-button header-admin-mobile-btn"
              onClick={() => navigate("/admin")}
              title="Users"
            >
              <Users size={14} />
            </button>
            <button
              className="header-myspace-mobile-button header-admin-mobile-btn"
              onClick={() => navigate("/admin/content")}
              title="Content"
            >
              <FileText size={14} />
            </button>
          </div>
        )}
        {currentUser?.role === "superadmin" && (
          <div className="header-admin-buttons">
            <div className="header-circle-buttons">
              {showSuperAdminStats && (
                <button
                  className="header-circle-btn"
                  onClick={() => navigate("/superadmin/stats")}
                  title="Stats"
                >
                  Stats
                </button>
              )}
              <button
                className="header-circle-btn"
                onClick={() => navigate("/superadmin")}
                title="Admins"
              >
                Admins
              </button>
            </div>
            <button
              className="header-myspace-mobile-button header-superadmin-mobile-btn"
              onClick={() => navigate("/superadmin")}
              title="Admins"
            >
              <Shield size={14} />
            </button>
            {showSuperAdminStats && (
              <button
                className="header-myspace-mobile-button header-superadmin-mobile-btn"
                onClick={() => navigate("/superadmin/stats")}
                title="Stats"
              >
                <BarChart3 size={14} />
              </button>
            )}
          </div>
        )}
        {!currentUser && (
          <>
            <Link to="/signup">
              <Button className="button-btn button-btn-green">Sign up</Button>
            </Link>
            <Link to="/login">
              <Button className="button-btn button-btn-blue">Login</Button>
            </Link>
          </>
        )}
      </div>
    </header>
  );
};

export default Header;
